import Phaser from 'phaser';

export default class Friend extends Phaser.Physics.Arcade.Sprite {
  constructor(scene, x, y, spriteKey, friendName, followDistance = 10) {
    super(scene, x, y, spriteKey);
    
    this.scene = scene;
    this.friendName = friendName;
    this.spriteKey = spriteKey;
    this.hypnotized = true; // Friends start hypnotized
    
    // Add to scene and enable physics
    this.scene.add.existing(this);
    this.scene.physics.add.existing(this);
    
    // Set physics properties
    this.body.setSize(8, 8, false); // Width, height, and whether to center
    this.body.setOffset(4, 24); // Offset the collision body to the bottom part (feet area)
    this.body.setCollideWorldBounds(false);
    
    // Following properties
    this.followDistance = followDistance; // Steps behind player in history (customizable)
    this.speed = 96; // Same speed as player
    this.isFollowing = false;
    this.player = null;
    
    // Hopping animation properties
    this.isHopping = false;
    this.hopTimer = null;
    this.hopTween = null;
    this.originalY = y;
    this.hopFrame = 0; // 0 for south[0], 1 for south[3]
    
    this.setupAnimations();
    this.play(`${this.spriteKey}-idle-south`);
  } 
  
  setupAnimations() {
    // Create animations for this friend sprite using same layout as Player
    const animKey = this.spriteKey;
    
    // Character frames in tileset (4 columns per row)
    // Column 0 = West, Column 1 = South, Column 2 = North, Column 3 = East
    // Each character has 3 animation frames vertically (rows 0, 1, 2)

    const COLS = 4; // Tileset has 4 columns

    // Calculate frame numbers for each direction and animation frame
    const frames = {
      west: [0, 0 + COLS, 0 + (COLS * 2)],      // Column 0: frames 0, 4, 8
      south: [1, 1 + COLS, 1 + (COLS * 2)],     // Column 1: frames 1, 5, 9
      north: [2, 2 + COLS, 2 + (COLS * 2)],     // Column 2: frames 2, 6, 10
      east: [3, 3 + COLS, 3 + (COLS * 2)]       // Column 3: frames 3, 7, 11
    };
    
    // Walking animations (using all 3 frames for smooth animation)
    this.scene.anims.create({
      key: `${animKey}-walk-west`,
      frames: [
        { key: animKey, frame: frames.west[0] },
        { key: animKey, frame: frames.west[1] },
        { key: animKey, frame: frames.west[2] }
      ],
      frameRate: 8,
      repeat: -1
    });
    
    this.scene.anims.create({
      key: `${animKey}-walk-south`,
      frames: [
        { key: animKey, frame: frames.south[0] },
        { key: animKey, frame: frames.south[1] },
        { key: animKey, frame: frames.south[2] }
      ],
      frameRate: 8,
      repeat: -1
    });
    
    this.scene.anims.create({
      key: `${animKey}-walk-north`,
      frames: [
        { key: animKey, frame: frames.north[0] },
        { key: animKey, frame: frames.north[1] },
        { key: animKey, frame: frames.north[2] }
      ],
      frameRate: 8,
      repeat: -1
    });
    
    this.scene.anims.create({
      key: `${animKey}-walk-east`,
      frames: [
        { key: animKey, frame: frames.east[0] },
        { key: animKey, frame: frames.east[1] },
        { key: animKey, frame: frames.east[2] }
      ],
      frameRate: 8,
      repeat: -1
    });
    
    // Idle animations (using the middle frame of each direction)
    this.scene.anims.create({
      key: `${animKey}-idle-west`,
      frames: [{ key: animKey, frame: frames.west[1] }],
      frameRate: 1
    });
    
    this.scene.anims.create({
      key: `${animKey}-idle-south`,
      frames: [{ key: animKey, frame: frames.south[1] }],
      frameRate: 1
    });
    
    this.scene.anims.create({
      key: `${animKey}-idle-north`,
      frames: [{ key: animKey, frame: frames.north[1] }],
      frameRate: 1
    });
    
    this.scene.anims.create({
      key: `${animKey}-idle-east`,
      frames: [{ key: animKey, frame: frames.east[1] }],
      frameRate: 1
    });
  }
  
  startFollowing(player) {
    this.player = player;
    this.isFollowing = true;
    console.log(`${this.friendName} started following player`);
  }
  
  stopFollowing() {
    this.isFollowing = false;
    this.player = null;
    this.body.setVelocity(0);
    this.play(`${this.spriteKey}-idle-south`);
  }
  
  update() {
    if (!this.isFollowing || !this.player) {
      return;
    }
    
    // Get target position from player's history
    const targetPos = this.player.getHistoryPosition(this.followDistance);
    
    if (!targetPos) {
      return;
    }
    
    // Calculate distance to target
    const distance = Phaser.Math.Distance.Between(this.x, this.y, targetPos.x, targetPos.y);
    
    // Only move if we're far enough away to avoid jittering
    if (distance > 8) {
      // Calculate direction to target
      const angle = Phaser.Math.Angle.Between(this.x, this.y, targetPos.x, targetPos.y);
      
      // Set velocity towards target
      this.body.setVelocity(
        Math.cos(angle) * this.speed,
        Math.sin(angle) * this.speed
      );
      
      // Play appropriate walking animation based on direction
      const dx = targetPos.x - this.x;
      const dy = targetPos.y - this.y;
      
      if (Math.abs(dx) > Math.abs(dy)) {
        // Moving horizontally
        if (dx > 0) {
          this.play(`${this.spriteKey}-walk-east`, true);
        } else {
          this.play(`${this.spriteKey}-walk-west`, true);
        }
      } else {
        // Moving vertically
        if (dy > 0) {
          this.play(`${this.spriteKey}-walk-south`, true);
        } else {
          this.play(`${this.spriteKey}-walk-north`, true);
        }
      }
    } else {
      // Close enough to target, stop moving
      this.body.setVelocity(0);
      
      // Play idle animation based on last movement direction
      if (this.anims.currentAnim) {
        const currentAnim = this.anims.currentAnim.key;
        if (currentAnim.includes('walk-west')) {
          this.play(`${this.spriteKey}-idle-west`);
        } else if (currentAnim.includes('walk-east')) {
          this.play(`${this.spriteKey}-idle-east`);
        } else if (currentAnim.includes('walk-north')) {
          this.play(`${this.spriteKey}-idle-north`);
        } else if (currentAnim.includes('walk-south')) {
          this.play(`${this.spriteKey}-idle-south`);
        }
      }
    }
  }
  
  startHopping() {
    if (this.isHopping) return; // Already hopping
    
    this.isHopping = true;
    this.originalY = this.y;
    this.hopFrame = 0;
    
    // Calculate frame numbers for hopping (south direction frames 0 and 3)
    const COLS = 4;
    const southFrames = [1, 1 + COLS, 1 + (COLS * 2)]; // [1, 5, 9]
    const hopFrames = [southFrames[0], southFrames[2]]; // frames 1 and 9 (south[0] and south[2])
    
    console.log(`${this.friendName} started hopping with frames:`, hopFrames);
    
    // Set initial frame
    this.setFrame(hopFrames[0]);
    
    // Create hopping tween (up and down motion)
    this.hopTween = this.scene.tweens.add({
      targets: this,
      y: this.originalY - 5, // Hop up 5 pixels
      duration: 300,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
      onYoyo: () => {
        // Toggle frame when reaching peak (going down)
        this.hopFrame = (this.hopFrame + 1) % 2;
        this.setFrame(hopFrames[this.hopFrame]);
      },
      onRepeat: () => {
        // Toggle frame when reaching bottom (going up)
        this.hopFrame = (this.hopFrame + 1) % 2;
        this.setFrame(hopFrames[this.hopFrame]);
      }
    });
  }
  
  stopHopping() {
    if (!this.isHopping) return;
    
    this.isHopping = false;
    
    // Stop and cleanup tween
    if (this.hopTween) {
      this.hopTween.stop();
      this.hopTween = null;
    }
    
    // Reset position and animation
    this.y = this.originalY;
    this.play(`${this.spriteKey}-idle-south`);
    
    console.log(`${this.friendName} stopped hopping`);
  }

  dehypnotize() {
    this.hypnotized = false;
    console.log(`${this.friendName} has been dehypnotized!`);
  }
}
